import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { updateStudioAPI } from '~entities/studios/api/update-studio';

import { ImageCropperDialog } from '~shared/components/image-cropper-dialog';
import { useImageCropper } from '~shared/hooks/useImageCropper';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

import { ImageUploader } from './logo-uploader';

export function StudioProfileForm({ currentUrl }: { currentUrl?: string }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: updateStudioAPI,
    onSuccess: () => {
      // Invalidate and refetch studio details to get the new image URL
      void queryClient.invalidateQueries({ queryKey: ['studio-details'] });
    },
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(
    currentUrl ?? null,
  );
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  const getCacheBustedUrl = useCallback((url: string): string => {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${Date.now()}`;
  }, []);

  const handleCropCompleteCallback = useCallback(
    (croppedFile: File, croppedImageUrl: string) => {
      setLogoFile(croppedFile);
      setLogoPreview(croppedImageUrl);
    },
    [],
  );

  const {
    cropperOpen,
    cropperImageSrc,
    handleImageChange: handleCropperImageChange,
    handleCropComplete,
    setCropperOpen,
  } = useImageCropper({
    onCropComplete: handleCropCompleteCallback,
  });

  // Update logoPreview when currentUrl changes (after refetch)
  useEffect(() => {
    if (currentUrl) {
      setLogoPreview(getCacheBustedUrl(currentUrl));
    }
  }, [currentUrl, getCacheBustedUrl]);

  async function handleSubmit() {
    if (!logoFile) {
      toast.error('No image selected. Please select a logo image to upload.');
      return;
    }

    setIsSubmitting(true);

    try {
      await mutation.mutateAsync({
        file: logoFile,
      });
      toast.success('Your studio logo has been updated successfully.');
      // Reset the file selection after successful upload

      if (currentUrl) {
        setLogoPreview(getCacheBustedUrl(currentUrl));
      }

      setLogoFile(null);
    } catch (error) {
      console.error(error);
      toast.error('There was a problem updating your logo.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleImageChange = (file: File | null) => {
    if (!file) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setLogoFile(null);
      setLogoPreview(null);
      return;
    }
    handleCropperImageChange(file);
  };

  useEffect(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    if (logoPreview && logoPreview.startsWith('blob:')) {
      blobUrlRef.current = logoPreview;
    }

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [logoPreview]);

  return (
    <>
      <Card className='mx-auto w-full max-w-2xl'>
        <CardHeader className='space-y-4 pb-6'>
          <div className='flex items-center justify-between'>
            <div className='space-y-2'>
              <CardTitle className='bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-2xl font-semibold text-transparent'>
                Studio Logo
              </CardTitle>
              <p className='text-sm leading-relaxed text-muted-foreground'>
                Upload your studio logo to personalize your profile and brand
                presence
              </p>
            </div>
            <div className='inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700'>
              <Upload className='mr-1 size-3' />
              Media
            </div>
          </div>
        </CardHeader>
        <CardContent className='pt-4 sm:pt-6'>
          <div className='space-y-2'>
            {/* <div className='text-sm font-medium sm:text-base'>Studio Logo</div> */}
            <ImageUploader
              currentImage={logoPreview}
              onImageChange={handleImageChange}
              maxWidth='100%'
              helpText='Upload a logo image (only JPEG and PNG formats are allowed)'
              showChangeButtonOverlay={true}
            />
            <p className='text-xs text-muted-foreground sm:text-sm'>
              This will be displayed on your studio profile and across the
              platform
            </p>
          </div>
        </CardContent>
        <CardFooter className='flex justify-end pb-4 sm:pb-6'>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardFooter>
      </Card>

      {cropperImageSrc && cropperOpen && (
        <ImageCropperDialog
          open={cropperOpen}
          onOpenChange={setCropperOpen}
          imageSrc={cropperImageSrc}
          onCropComplete={handleCropComplete}
          aspectRatio={1}
          title='Crop Studio Logo'
          description='Adjust your logo image to the desired crop area'
        />
      )}
    </>
  );
}
